import { IToggle } from '@unleash/nextjs';

import { ICommerceAsset } from 'src/services/responseTypes/commerceResponse';
import {
  BestOffering,
  PlansMetaData
} from 'src/services/responseTypes/plansMetaDataResponse';
import { purchasePlanResponse } from 'src/services/responseTypes/purchasePlanResponse';
import { purchaseResponse } from 'src/services/responseTypes/rentalPurchaseResponse';
import { SubscriptionApiResponse } from 'src/services/responseTypes/subscriptionApiResponse';
import { TopSearchType } from 'src/services/responseTypes/topSearchResponse';

import { DynamicAdsResponse } from '../services/responseTypes/dynamicAdsResponse';
import { SingleLaunchResponse } from '../services/responseTypes/singleLaunchResponse';
import { XtraApiResponse } from '../services/responseTypes/xtraApiResponse';
import { DynamicObject, UserWatchlistType } from '../utils/commonTypes';

export interface AppConfigType {
  deviceType?: string;
  deviceWidth?: number;
  userType?: string;
  pageType?: string;
  isRequestedByBot?: boolean;
  isRequestedByPartner?: boolean;
  isViUser?: boolean;
  pages?: string;
  isWebPCompatible?: boolean;
  isAvifCompatible?: boolean;
  appHostname?: string;
  actualDeviceType?: string;
}

export interface BackToPartnerConfig {
  text: string;
  link: string;
}
export interface BlockerScreenConfig {
  banner: string;
  title: string;
  description: string;
  primary_cta: PrimaryCta;
}
export interface PrimaryCta {
  text: string;
  deepLink: string;
  webLink: string;
  viServiceWebLink: string;
}
export interface PartnerConfig {
  isPartnerActive: boolean;
  showBackToPartner: boolean;
  backToPartnerConfig: BackToPartnerConfig;
  blockerScreenConfig: BlockerScreenConfig;
}

interface Partner {
  partner_id: number;
  partner_name: string;
  config: PartnerConfig;
}

export interface UsersPreferenceData {
  [x: string]: any;
  auto_play?: boolean;
  display_language?: string;
  content_language?: string;
  download_over_wifi?: string;
  download_quality?: string;
  first_time_login?: string;
  gdpr_policy?: [];
  parental_control?: object;
  parental_control_v2?: string;
  popups?: [];
  recent_search?: string;
  stream_over_wifi?: '';
  streaming_quality?: string;
  partner?: Partner;
}
export type PopupsType = {
  showLanguageBanner?: boolean;
  showLanguagePopup?: boolean;
  isViTvod?: boolean;
  isAvodPopupNudgeVisible?: boolean;
  showReferralSubscriptionNudge?: boolean;
  referralRedirectLink?: string;
  referralSourceElement?: string;
};

export interface GenderJson {
  gender: string;
  buildVersion: string;
  lastUpdatedTime: string;
}
export interface DobJson {
  birthDate: string;
  buildVersion: string;
  lastUpdatedTime: string;
}
export interface AgeJson {
  age_range: string;
}

export interface Additional {
  Source: string;
  original_user_agent: string;
  'X-Forwarded-For': string;
  genderJson: GenderJson;
  dobJson: DobJson;
  ageJson: AgeJson;
}
export interface UsersData {
  id?: string;
  system?: string;
  email?: string;
  email_verified?: boolean;
  mobile?: string;
  mobile_verified?: null;
  first_name?: string;
  last_name?: string;
  mac_address?: string;
  birthday?: string;
  gender?: string;
  activation_date?: string;
  activated?: boolean;
  ip_address?: string;
  registration_country?: string;
  registration_region?: string;
  additional?: Additional;
  lastlogin?: string;
  is_password_attached?: boolean;
  packType?: string;
  consents?: Consents;
}

interface Consents {
  IsEmailNotificationEnabled: boolean;
  IsSMSNotificationEnabled: boolean;
  IsWhatsappNotificationEnabled: boolean;
  IsPushNotificationEnabled: boolean;
  IsThirdPartyDataSharing: boolean;
  IsThirdPartyWatchHistoryDataSharing: boolean;
  IsTargetedAds: boolean;
  IsPrivacyPolicy: boolean;
}

export interface ComboOfferState {
  isComboOfferEnabled?: boolean;
  isComboOfferEnabledPending?: null | boolean;
}

export interface seoMetaAssetsType {
  [key: string]: {
    title: string;
    description: string;
  };
}
export interface CompanionAdState {
  isAdPlayingOnPlayer?: boolean;
  companionAdLoaded?: boolean;
}

export interface PaymentProvider {
  name: string;
}

export interface PlanDetails {
  id: string;
  asset_type: number;
  subscription_plan_type: string;
  title: string;
  original_title: string;
  system: string;
  description: string;
  billing_cycle_type: string;
  billing_frequency: number;
  price: number;
  currency: string;
  country: string;
  countries: string[];
  start: string;
  end: string;
  only_available_with_promotion: boolean;
  recurring: boolean;
  payment_providers: PaymentProvider[];
  promotions: any[];
  asset_types: number[];
  asset_ids: string[];
  number_of_supported_devices: number;
  tier: string;
  movie_audio_languages: any[];
  tv_show_audio_languages: any[];
  channel_audio_languages: any[];
  duration_text: string;
  duration: string;
  allowed_playback_duration: number;
  valid_for_all_countries: boolean;
  terms_and_conditions: string;
  billing_type: string;
  business_type: string;
  category: string;
}
interface TvodPriceObj {
  currency: string;
  price: number;
}
export interface TvodState {
  isTvodContent?: boolean;
  tvodPriceObj?: TvodPriceObj | null;
  planDetails?: PlanDetails[] | null;
  tvodOverlay?: boolean;
  isTVODLogicResolved?: boolean;
  isTvodEvent?: boolean;
  isUserEntitledForTvod?: boolean;
}

export interface GamificationConfig {
  onboardingBackgroundImage: string;
  onboardingBackgroundImageDesktop: string;
  quizPageBackground: string;
  quizPageBackgroundDesktop: string;
  bannerBackgroundImage: string;
  bannerLogoImage: string;
  quizTitleImage: string;
  quizPageTitle: string;
  quizInstructions: string[];
  ctaText: string;
  cta2Text: string;
  termsAndConditionsUrl: string;
  howToPlayUrl: string;
  resultInfoText: string;
  resultScreenImage: string;
  sponsorLogo: string;
  sponsorText: string;
  sponsorDeeplink: string;
  resultScreenVideoPWA: string;
}

export enum GamificationCampaignStatus {
  IDLE = 'IDLE',
  SCHEDULED = 'SCHEDULED',
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED'
}

interface GcpError {
  code: string;
  message: string;
}

export enum GamificationScreens {
  Landing = 'Landing',
  Quiz = 'Quiz',
  Result = 'Result',
  Winners = 'Winners'
}

export interface GamificationState {
  config?: GamificationConfig | null;
  isQuizActive?: boolean;
  settings?: DynamicObject;
  questions?: any[];
  gameId?: string | null;
  currentScreen?: GamificationScreens;
  selectedAnswerId?: string | null;
  campaignStatus?: GamificationCampaignStatus;
}

export interface AppState {
  apiErrorResponse:
    | { apiIdentificationCode: string; code: string; message: string }
    | { [key: string]: DynamicObject };
  isUserLoggedIn: boolean;
  initialLaunchData: SingleLaunchResponse | null;
  usersPreferenceData: UsersPreferenceData | null;
  usersData: UsersData | null;
  translations: { [key: string]: DynamicObject };
  userWatchlist: UserWatchlistType;
  remoteConfig: IToggle[] | [];
  comboOfferState: ComboOfferState;
  usersLocationData: XtraApiResponse | null;
  appConfig: AppConfigType;
  displayAdsData: DynamicAdsResponse | null;
  parentalData: { [key: string]: boolean };
  tvodRentals: TvodRentals;
  subscriptionData: subscriptionDataType;
  popups: PopupsType;
  topSearch: TopSearchType[];
  plansMetaData: PlansMetaData[] | null;
  setPlansMetaData: (data: PlansMetaData[]) => void;
  searchLandingData: any;
  // seoMetaAssets: seoMetaAssetsType | null;
  loadExternalScripts: boolean;
  companionAdState: CompanionAdState;
  tvodState: TvodState;
  gcpError: GcpError;
  isGcpErrorShown: boolean;
  commerceData: Array<CommerceData>;
  commerceComponentData: Array<ICommerceAsset>;
  commerceWishlistedProductsLength: number;
  pageLoadPingUrl: string;
  bestOfferingPlan: BestOffering | null;
  upgradeData?: DynamicObject;
  gamificationState: GamificationState;
}

export interface TvodRentals {
  activeRentalPurchases: purchaseResponse[];
  activeAssetIDs: string[];
  expiredRentalPurchases: purchaseResponse[];
  expiredAssetIDs: string[];
  allRentalPurchases: purchaseResponse[];
  uniqueRentalPurchases: purchaseResponse[];
  tvodPlans?: purchasePlanResponse;
  getPlansSuccess: boolean;
}

export interface subscriptionDataType {
  currentActiveSubscriptions: SubscriptionApiResponse[];
  maxPricePack: SubscriptionApiResponse | any;
}

export interface CommerceData {
  timeStamp: string;
  data: Array<ICommerceAsset>;
}

export interface AppActions {
  setErrorData: (data: { [key: string]: DynamicObject }) => void;
  setIsUserLoggedIn: (data: boolean) => void;
  setInitialLaunchData: (data: SingleLaunchResponse) => void;
  setUsersData: (data: UsersData) => void;
  setUserPackType: (data: string) => void;
  setUsersPreferenceData: (data: UsersPreferenceData) => void;
  setTranslations: (data: { [key: string]: DynamicObject }) => void;
  setRemoteConfig: (data: IToggle[]) => void;
  setComboOfferState: (data: ComboOfferState) => void;
  setUsersLocationData: (data: XtraApiResponse) => void;
  setAppConfig: (data: AppConfigType) => void;
  setUserWatchlist: (data: UserWatchlistType) => void;
  setDisplayAdsData: (data: DynamicAdsResponse) => void;
  setParentalData: (data: { [key: string]: boolean }) => void;
  setTvodRentals: (data: TvodRentals) => void;
  setSubscriptionData: (data: subscriptionDataType) => void;
  setPopups: (data: PopupsType) => void;
  setTopSearch: (data: TopSearchType[]) => void;
  setSearchLandingData: (data: any) => void;
  setExternalScripts: (data: boolean) => void;
  setCompanionAdState: (data: CompanionAdState) => void;
  setCommerceData: (data: CommerceData) => void;
  clearCommerceData: () => void;
  setCommerceComponentData: (data: Array<ICommerceAsset>) => void;
  clearCommerceComponentData: () => void;
  setCommerceWishlistedProductsLength: (data: number) => void;
  setTvodState: (data: TvodState) => void;
  setGcpError: (data: GcpError) => void;
  setIsGcpErrorShown: (data: boolean) => void;
  setPageLoadPingUrl: (data: string) => void;
  setBestOfferingPlan: (data: BestOffering) => void;
  setUpgradeData: (data: DynamicObject) => void;
  setGamificationState: (data: GamificationState) => void;
}
